import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { Package } from '@renderer/types/configState'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

const Checkout: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, uploadSessionState, logActivity, userPreferences } =
    useSessionStateStore()
  const { config, frames, frameCategories } = useConfigStore()
  const projectCurrency = normalizeCurrency(config?.resolvedCurrency)
  const [quantity, setQuantity] = useState(1)
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null)

  // Get the category image based on frame selection
  const getCategoryImage = (): string => {
    // Try to get frame category from selected frame
    if (userPreferences.frameId) {
      const selectedFrame = frames.find((frame) => frame._id === userPreferences.frameId)
      if (selectedFrame) {
        // Check if frame belongs to a category
        const category = frameCategories.find((cat) =>
          cat.frames?.some((frame) => frame._id === selectedFrame._id)
        )

        if (category) {
          const categoryName = category.name.toLowerCase()
          if (categoryName.includes('3cut')) {
            return '/assets/yotta/frame-3cut.webp'
          } else if (categoryName.includes('4cut1')) {
            return '/assets/yotta/frame-4cut1.webp'
          } else if (categoryName.includes('4cut2')) {
            return '/assets/yotta/frame-4cut2.webp'
          }
        }
      }
    }

    // Default to 3cut
    return '/assets/yotta/frame-3cut.webp'
  }

  // Get packages from config
  const packages = config?.pages?.checkout?.settings?.packages || []
  const printPackages = packages
    .filter((pkg) => pkg.type === 'print')
    .sort((a, b) => (a.quantity || 0) - (b.quantity || 0))

  // Initialize with first print package
  useEffect(() => {
    if (printPackages.length > 0 && !selectedPackage) {
      const initialPackage = printPackages[0] // Always use first package
      setSelectedPackage(initialPackage)
      setQuantity(initialPackage.quantity || 1)
    }
  }, [])

  // Update package when quantity changes
  useEffect(() => {
    if (printPackages.length > 0) {
      const newPackage = printPackages.find((pkg) => pkg.quantity === quantity) || printPackages[0]
      setSelectedPackage(newPackage)

      // Update session state
      setSessionState({
        userPreferences: {
          copies: quantity
        },
        transaction: {
          amount: newPackage.price || 0,
          selectedPackages: [newPackage]
        }
      })
    }
  }, [quantity])

  const handleQuantityChange = (newQuantity: number): void => {
    const maxQuantity = printPackages[printPackages.length - 1]?.quantity || 10
    const validQuantity = Math.max(1, Math.min(maxQuantity, newQuantity))
    setQuantity(validQuantity)

    logActivity(`Quantity changed to: ${validQuantity}`)
  }

  const handleNext = (): void => {
    const packageToUse = selectedPackage || printPackages[0]

    const details = {
      quantity,
      package: packageToUse?.description,
      amount: packageToUse?.price
    }

    logActivity('Checkout completed', details)

    // Delay session upload to allow more activity logs to be collected
    setTimeout(() => {
      uploadSessionState()
    }, 5000) // 5 second delay

    // Navigate to payment
    navigate(`/${projectId}/payment`)
  }

  const price = selectedPackage?.price || 0

  return (
    <div className="w-full h-full bg-white flex items-center justify-center">
      {/* Left Side - Category Image */}
      <div className="flex items-center justify-center p-16 w-1/4">
        <div className="w-full max-w-lg">
          <img
            src={getCategoryImage()}
            alt="Selected Frame Category"
            className="w-full object-contain drop-shadow-xl"
          />
        </div>
      </div>

      {/* Right Side - Quantity and Price */}
      <div className="flex flex-col justify-center items-center p-16">
        <div className="text-center space-y-12">
          {/* Title */}
          <h1 className="font-title-yotta text-blueGrayYotta text-[96px] font-bold drop-shadow-lg">
            Jumlah Cetak
          </h1>

          {/* Quantity Controls */}
          <div className="flex items-center justify-center space-x-8">
            <button
              onClick={() => handleQuantityChange(quantity - 1)}
              disabled={quantity <= 1}
              className="w-16 h-16 bg-blueGrayYotta text-white text-3xl font-bold rounded-lg shadow-lg hover:shadow-xl transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              −
            </button>

            <span className="text-6xl font-bold text-blueGrayYotta min-w-[100px] text-center">
              {quantity}
            </span>

            <button
              onClick={() => handleQuantityChange(quantity + 1)}
              className="w-16 h-16 bg-blueGrayYotta text-white text-3xl font-bold rounded-lg shadow-lg hover:shadow-xl transition-all active:scale-95"
            >
              +
            </button>
          </div>

          {/* Price Box */}
          <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-8 shadow-lg">
            <div className="text-center">
              <p className="text-4xl font-bold text-blueGrayYotta">
                {formatAmount(price, projectCurrency)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Next Button */}
      <div className="absolute bottom-12 right-24">
        <button
          onClick={handleNext}
          className={`font-body-yotta text-blueGrayYotta text-[32px] hover:opacity-80 transition-opacity active:scale-95 transform`}
        >
          Next →
        </button>
      </div>
    </div>
  )
}

export default Checkout
